import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { setPendingGoogleSignup, type GoogleSignupIdentity } from '@/lib/google-signup';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { config } from '@/lib/config';
import { track } from '@/lib/analytics';
import { chromeIntent, isAndroid } from '@/lib/in-app-browser';
import { errorMessage } from '@/lib/utils';
import { useTheme } from '@/app/theme';
import { Skeleton } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/system';

interface GoogleId {
  initialize: (options: Record<string, unknown>) => void;
  renderButton: (el: HTMLElement, options: Record<string, unknown>) => void;
  prompt: () => void;
  cancel: () => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleId } };
  }
}

let gisScript: Promise<void> | null = null;

function loadGoogleIdentity(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!gisScript) {
    gisScript = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        gisScript = null;
        reject(new Error('Google sign-in could not load'));
      };
      document.head.appendChild(s);
    });
  }
  return gisScript;
}

async function makeNonce(): Promise<[raw: string, hashed: string]> {
  const raw = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return [raw, hashed];
}

/** Google sign-in inside an app's in-app browser (Facebook, Instagram…): send the page to a real browser. */
export function GoogleInBrowser({ intent = 'login' }: { intent?: 'login' | 'signup' }) {
  if (!isAndroid())
    return (
      <p className="text-center text-sm text-fg-muted">
        To continue with Google, open this page in your browser: tap ⋯, then Open in browser.
      </p>
    );
  return (
    <Button asChild variant="secondary" size="lg" className="w-full">
      <a
        href={chromeIntent(window.location.href)}
        onClick={() => track('google_browser_open', { intent, method: 'google' })}
      >
        <img src="/google-mark.svg" className="size-5 shrink-0" alt="" />
        Continue with Google in Chrome
      </a>
    </Button>
  );
}

export function GoogleButton({
  text = 'continue_with',
  intent = 'login',
  onSignup,
}: {
  text?: 'continue_with' | 'signup_with' | 'signin_with';
  intent?: 'login' | 'signup';
  onSignup?: (identity: GoogleSignupIdentity) => void;
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const beginSignup = (identity: GoogleSignupIdentity) => {
    if (onSignup) onSignup(identity);
    else {
      setPendingGoogleSignup(identity);
      navigate(params.toString() ? `/signup?${params}` : '/signup');
    }
  };
  const beginSignupRef = useRef(beginSignup);
  beginSignupRef.current = beginSignup;
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [missing, setMissing] = useState<GoogleSignupIdentity | null>(null);
  const { dark } = useTheme();

  useEffect(() => {
    if (config.isDemo) return;
    let cancelled = false;
    setReady(false);
    setFailed(false);

    (async () => {
      try {
        let clientId = config.googleClientId;
        if (!clientId) {
          const response = await fetch('/api/auth/google/config');
          const data = (await response.json()) as { clientId?: string };
          if (!response.ok || !data.clientId) throw new Error('Google sign-in could not load');
          clientId = data.clientId;
        }
        await loadGoogleIdentity();
        const gid = window.google?.accounts?.id;
        const el = containerRef.current;
        if (cancelled) return;
        if (!gid || !el) throw new Error('Google sign-in could not load');
        const [rawNonce, hashedNonce] = await makeNonce();
        if (cancelled) return;
        gid.initialize({
          client_id: clientId,
          nonce: hashedNonce,
          use_fedcm_for_prompt: true,
          itp_support: true,
          auto_select: false,
          callback: async (response: { credential?: string }) => {
            if (cancelled || !response.credential) return;
            const pending = toast.loading('Signing you in with Google…');
            try {
              track(intent === 'signup' ? 'signup_google_check' : 'login_attempt', { method: 'google' });
              const { missingAccount } = await api.auth.signInWithGoogle(
                response.credential,
                rawNonce,
                'login',
              );
              if (!missingAccount) track('login', { method: 'google' });
              if (cancelled) {
                toast.dismiss(pending);
                return;
              }
              if (missingAccount) {
                if (intent === 'login') track('login_account_not_found', { method: 'google' });
                const identity = { ...missingAccount, credential: response.credential, nonce: rawNonce };
                if (intent === 'signup') beginSignupRef.current(identity);
                else setMissing(identity);
                toast.dismiss(pending);
                return;
              }

              toast.dismiss(pending);
            } catch (err) {
              track('login_failed', { method: 'google', reason: 'request_failed' });
              toast.error(errorMessage(err, 'Google sign-in failed. Please try again.'), { id: pending });
            }
          },
        });
        el.innerHTML = '';
        gid.renderButton(el, {
          type: 'standard',
          // Google's dark outline keeps the logo on the button background,
          // without the white logo tile used by filled_black.
          theme: dark ? 'outline_dark' : 'outline',
          size: 'large',
          shape: 'rectangular',
          text,
          logo_alignment: 'center',
          width: Math.max(220, Math.min(400, el.offsetWidth || 380)),
        });
        setReady(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dark, text, intent, attempt]);

  if (config.isDemo || failed) {
    return (
      <>
        <Button
          variant="secondary"
          size="lg"
          className="w-full"
          loading={busy}
          onClick={async () => {
            if (!config.isDemo) {
              setFailed(false);
              setAttempt((value) => value + 1);
              return;
            }
            setBusy(true);
            try {
              const { isNewUser } = await api.auth.signInWithGoogle(undefined, undefined, intent);
              if (config.isDemo) track(isNewUser ? 'sign_up' : 'login', { method: 'google' });
            } catch (err) {
              track('login_failed', { method: 'google', reason: 'request_failed' });
              toast.error(errorMessage(err, 'Google sign-in failed. Please try again.'));
            } finally {
              setBusy(false);
            }
          }}
        >
          <img src="/google-mark.svg" className="size-5 shrink-0" alt="" />
          {failed
            ? 'Retry Google sign-in'
            : text === 'signin_with'
              ? 'Sign in with Google'
              : text === 'signup_with'
                ? 'Sign up with Google'
                : 'Continue with Google'}
        </Button>
      </>
    );
  }

  return (
    <>
      <div className="relative h-10">
        {!ready && <Skeleton className="absolute inset-0 h-10 rounded-md" />}
        <div
          ref={containerRef}
          className="auth-google-button flex h-10 justify-center overflow-hidden [color-scheme:light]"
        />
      </div>
      <Modal
        open={!!missing}
        onClose={() => {
          setMissing(null);
        }}
        title="Account does not exist"
        description="This Google account isn’t registered with CallSync yet."
        busy={busy}
      >
        <p className="break-all text-sm font-medium text-fg">{missing?.email}</p>
        <p className="mt-3 text-sm text-fg-muted">
          Create an account with this email, or try another Google account.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <Button
            className="w-full"
            loading={busy}
            onClick={() => {
              if (!missing || busy) return;
              beginSignup(missing);
              setMissing(null);
            }}
          >
            Create account
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            disabled={busy}
            onClick={() => {
              setMissing(null);
              window.google?.accounts?.id?.cancel();
              window.google?.accounts?.id?.disableAutoSelect();
              window.google?.accounts?.id?.prompt();
              containerRef.current?.querySelector<HTMLElement>('[role="button"]')?.focus();
            }}
          >
            Try Another Account
          </Button>
        </div>
      </Modal>
    </>
  );
}
